import * as React from 'react';
import { Checkbox, IconButton, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { ElementDimensions } from '../../styles/ElementDimensions';
import TextField, { TextFieldChangeHandler, TextFieldLabelPosition, TextFieldProps } from '../common/TextField';
import SplitProgress, { SplitProgressProps } from './SplitProgress'
import { CheckSummary } from '../../models/common/CheckSummary';
import { CheckState, CheckStateContext } from '../../providers/CheckStateContextProvider';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { CheckClaimSummary } from '../../models/common/CheckClaimSummary';
import themePrimary from '../../styles/themePrimary';
import { String_Split_SplitByAmountPayLabel, String_Split_SplitByAmountOfLabel, 
    String_Split_SplitByAmountClaimSummaryString, 
    String_MultipleChecks_CheckNumberLabel,
    String_MultipleChecks_CheckTotalLabel,
    String_System_Pay,
    String_System_ConnectionError,
    String_System_GeneralError,
    String_System_View,
    String_Split_SplitBySeatInstruction} from '../../utilities/StringHelper';
import MiniButton, { MiniButtonProps } from '../common/Buttons/MiniButton';
import ViewCheckDialog, { ViewCheckDialogProps } from '../common/ViewCheckDialog';
import { CheckFlat } from '../../models/common/CheckFlat';
import { SplitType } from '../../models/common/SplitType';
import { useHistory } from 'react-router';
import axios, { CancelTokenSource } from 'axios';
import { GetCheckRequest } from '../../models/checkout/GetCheckRequest';
import { GetCheckResponse } from '../../models/checkout/GetCheckResponse';
import { ErrorCode } from '../../models/common/ErrorCode';
import { CheckOutType } from '../../models/common/CheckOutType';
import { NavigateCheckOutStart } from '../common/NavigateCheckOutStart';
import { Skin } from '../../models/common/Skin';

const useStyles = makeStyles({
    checkBox: {
        color: props => (props as Skin).primary_main_color + ' !important',
    },
    checkContainer: {
        borderRadius: '6px',
        backgroundColor: themePrimary.palette.grey[200],
        margin: ElementDimensions.margin10 + 'px',
    },
    checkNumberLabel: {
        marginTop: 'auto',
        marginBottom: 'auto',
    },
    headerContainer: {
        backgroundColor: themePrimary.palette.grey[200],
        padding: ElementDimensions.padding5 + 'px ',
        borderRadius: '6px',
        display: 'flex',
    },
    headerGrid: {
        display: 'grid',
        gridTemplateColumns: 'calc(12vw - 20px) calc(58vw - 20px) calc(30vw - 20px)',
        marginLeft: 'auto',
        marginRight: ElementDimensions.margin10 + 'px',
    },
    headerGridLandscape: {
        display: 'grid',
        gridTemplateColumns: 'calc(30vw - 10px) calc(70vw - 10px)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    headerViewCheckButton: {
        textAlign: 'left',
    },
    innerContainer: {
        display: 'flex',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    instructionText: {
        textAlign: 'center',
        marginLeft: ElementDimensions.margin5 + 'px',
        marginRight: ElementDimensions.margin5 + 'px',
        marginBottom: ElementDimensions.margin10 + 'px ',
    },
    outerContainer: {
        width: '100%',
        display: 'flex',
    },
    root: {
        padding: ElementDimensions.padding10 + 'px',
        backgroundColor: themePrimary.palette.grey[100],
        width: '91%',
    },
});

export type SplitBySeatChangeHandler = (index: number) => any;

export interface SplitBySeatProps {
    checkSummaries: CheckSummary[];
    onChangeHandler: SplitBySeatChangeHandler;
}

const SplitBySeat = (props: SplitBySeatProps) => {
    let history = useHistory();
    const { checkSummaries, onChangeHandler } = props;
    const [selectedCheckSummary, setSelectedCheckSummary] = React.useState<CheckSummary | undefined>(undefined);
    const [selectedCheckDetail, setSelectedCheckDetail] = React.useState<CheckFlat | null>(null);  
    const [selectedCheckIndex, setSelectedCheckIndex] = React.useState<number | undefined>(undefined);  
    const [viewCheckDialogOpen, setViewCheckDialogOpen] = React.useState<boolean>(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const isPortrait = true;    
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const checkState = React.useContext(CheckStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    const handleViewCheckButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        let checkSummary = checkSummaries.find(c => c.id.toString() === event.currentTarget.value);
        if (checkSummary !== null && checkSummary !== undefined) {
            setSelectedCheckSummary(checkSummary)
            getCheck(axios.CancelToken.source(), checkSummary)
            event.stopPropagation()
        }
    }

    const viewCheckButtonProps: MiniButtonProps = {
        value:'',
        disabled: false,
        label: String_System_View.find(s => s.key === userState.state.locale)!!.value,
        onClickHandler: handleViewCheckButtonClick,
    }

    const handleViewCheckPayButtonClick = (event: React.MouseEvent<HTMLElement>, check: CheckSummary) => {
        const newCheckState: CheckState = checkState.state;
        newCheckState.checkSummary = check;
        newCheckState.checkDetail = selectedCheckDetail!!;
        newCheckState.splitAmount = 0;
        newCheckState.splitType = SplitType.SplitBySeat;
        newCheckState.splitEvenlyCount = 0;
        newCheckState.splitPercentage = 0;
        newCheckState.paymentId = null;

        checkState.setState(newCheckState);

        history.push('/payment/pay');
    }

    const handleViewCheckDialogCloseClick = (event: React.MouseEvent<HTMLElement>) => {
        setViewCheckDialogOpen(false);
    }

    const viewCheckDialogProps: ViewCheckDialogProps = {
        checkSummary: selectedCheckSummary!!,
        checkDetail: selectedCheckDetail!!,
        open: viewCheckDialogOpen,
        showFooter: true,
        useClaimedAmounts: true,
        onPayClickHandler: handleViewCheckPayButtonClick,
        onCloseHandler: handleViewCheckDialogCloseClick,
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: false,
        onCloseHandler: handleErrorDialogClose,
    }

    const getCheck = async (cancelTokenSource: CancelTokenSource, check: CheckSummary) => {

        if (check !== undefined) {
            const request: GetCheckRequest = {
                user_id: userState.state.userId!!,
                use_claimed_amounts: true,
                release_claims: false,
            };        
    
            let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + check.id
    
            const headers = {
                'X-API-Key': checkOutState.state.restaurantApiKey,
                'location_id': checkOutState.state.restaurantId
            }
    
            axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
            .then(function(response) {
                let r = response.data as GetCheckResponse;
    
                if (r.success === true) {
                    setSelectedCheckDetail(r.detail)
                    setViewCheckDialogOpen(true)
                }
                else {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogOpen(true);
                }
            })
            .catch(function(error) {
                if (error.response) {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                }
                else {
                    setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                }
                
                setErrorDialogOpen(true);
            });
        }
    }

    const handleSelectCheckClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
        setSelectedCheckIndex(index)
        onChangeHandler(index)
    }

    const isChecked = (index: number): boolean => {
        if (selectedCheckIndex === index) {
            return true
        }
        else {
            return false
        }
    }

    return (
        <div className={classes.root}>

            <Typography variant="body1" className={classes.instructionText}>
                {String_Split_SplitBySeatInstruction.find(s => s.key === userState.state.locale)!!.value}
            </Typography>

            <div >
                    {
                        checkSummaries.map((check, index) => {
                            return (
                                <div key={'seatchecks-outer-container-' + check.number} className={classes.checkContainer} onClick={(e) => handleSelectCheckClick(e, index)}>
                                    <div className={classes.headerContainer} key={'seatchecks-header-outer-container-' + check.number}>
                                        <div className={isPortrait ? classes.headerGrid : classes.headerGridLandscape} key={'seatchecks-header-inner-container-' + check.number}>
                                            <div>
                                                <Checkbox
                                                    className={classes.checkBox}
                                                    checked={isChecked(index)}
                                                />
                                            </div>
                                            <div>
                                                <Typography variant="body1" key={'seatchecks-header-check-number-' + check.number} className={classes.checkNumberLabel}>
                                                    {String_MultipleChecks_CheckTotalLabel.find(s => s.key === userState.state.locale)!!.value} {check.amount_due_string}
                                                </Typography>
                                                <Typography variant="body1" key={'seatchecks-header-check-number2-' + check.number} className={classes.checkNumberLabel}>
                                                    {check.number}
                                                </Typography>
                                            </div>
                                            
                                            <div className={classes.headerViewCheckButton} key={'seatchecks-view-check-button-container-' + check.number}>
                                                <MiniButton
                                                    key={'seatchecks-view-check-button-' + check.number}
                                                    value={check.id.toString()}
                                                    disabled={viewCheckButtonProps.disabled}
                                                    label={viewCheckButtonProps.label}
                                                    onClickHandler={viewCheckButtonProps.onClickHandler} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
            </div>

            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />

            <ViewCheckDialog 
                checkSummary={viewCheckDialogProps.checkSummary}
                checkDetail={viewCheckDialogProps.checkDetail}
                open={viewCheckDialogProps.open}
                showFooter={viewCheckDialogProps.showFooter}
                useClaimedAmounts={viewCheckDialogProps.useClaimedAmounts}
                onPayClickHandler={viewCheckDialogProps.onPayClickHandler}
                onCloseHandler={viewCheckDialogProps.onCloseHandler} />

        </div>
    );
};

export default SplitBySeat;
